<template>
  <WCModal position="center" class="wc-order-type-cart-picker" @dismiss="onClose">
    <div class="wc-order-type-cart-picker__handle" aria-hidden="true">
      <span class="wc-order-type-cart-picker__handle-bar"></span>
    </div>

    <WCModalHeader class="wc-order-type-cart-picker__header">
      <h1 class="wc-order-type-cart-picker__title">{{ $t(titleKey, titleParams) }}</h1>
      <button
        type="button"
        class="wc-order-type-cart-picker__close"
        :aria-label="$t('close')"
        @click="onClose"
      >
        <font-awesome-icon :icon="['fas', 'xmark']" aria-hidden="true" />
      </button>
    </WCModalHeader>

    <p class="wc-order-type-cart-picker__item-name" v-html="$t(contextKey, contextParams)"></p>

    <WCModalBody class="wc-order-type-cart-picker__body">
      <ul class="wc-order-type-cart-picker__list">
        <WCOrderTypeCartOption
          v-for="orderType in orderTypes"
          :key="orderType.id"
          :item="item"
          :orderType="orderType"
          :variation="variation"
        />
      </ul>
      <p class="wc-order-type-cart-picker__reassure">
        <font-awesome-icon :icon="['fas', 'shield-halved']" aria-hidden="true" />
        {{ $t('orderTypeCartStaysUntouched', { orderTypeName: activeOrderType?.name ?? '' }) }}
      </p>
    </WCModalBody>
  </WCModal>
</template>

<script setup lang="ts">
import { computed, defineProps } from 'vue';
import WCModal from '@/modules/modals/components/WCModal/WCModal.vue';
import WCModalHeader from '@/modules/modals/components/WCModalHeader/WCModalHeader.vue';
import WCModalBody from '@/modules/modals/components/WCModalBody/WCModalBody.vue';
import WCOrderTypeCartOption from '@/modules/orderTypes/components/WCOrderTypeCartOption/WCOrderTypeCartOption.vue';
import useOrderTypes from '@/composables/useOrderTypes';
import ItemSummary from '@/models/entities/items/item-summary';
import OrderType from '@/models/entities/cart/order-type';

const props = defineProps<{
  close: () => void;
  dismiss: () => void;
  item: ItemSummary;
  orderTypes: OrderType[];
  variation?: string | null;
}>();

const { activeOrderType } = useOrderTypes();

const currentOrderTypeIsEligible = computed(() =>
  props.orderTypes.some(orderType => orderType.id === activeOrderType.value?.id),
);

const isSingleDestination = computed(
  () => !currentOrderTypeIsEligible.value && props.orderTypes.length === 1,
);

const titleKey = computed(() =>
  isSingleDestination.value ? 'addToOrderTypeCart' : 'addToWhichOrder',
);

const titleParams = computed(() =>
  isSingleDestination.value ? { orderTypeName: props.orderTypes[0]?.name ?? '' } : {},
);

const contextKey = computed(() =>
  currentOrderTypeIsEligible.value
    ? 'itemAvailableInMultipleCarts'
    : 'itemNotAvailableInCurrentCart',
);

const contextParams = computed(() =>
  currentOrderTypeIsEligible.value
    ? { itemName: props.item.name }
    : { orderTypeName: activeOrderType.value?.name ?? '' },
);

const onClose = (): void => props.dismiss();
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

@include media-breakpoint-up(md) {
  .wc-order-type-cart-picker :deep(.modal-dialog) {
    max-width: 27.5rem;
  }
}

.wc-order-type-cart-picker :deep(.modal-content) {
  border: none;
  border-radius: 1rem 1rem 0 0;
  overflow: hidden;
}

.wc-order-type-cart-picker :deep(.modal-dialog-centered.modal-dialog-scrollable .modal-content) {
  max-height: 50vh;
}

@include media-breakpoint-up(md) {
  .wc-order-type-cart-picker :deep(.modal-content) {
    border-radius: 1rem;
    overflow: visible;
  }

  .wc-order-type-cart-picker :deep(.modal-dialog-centered.modal-dialog-scrollable .modal-content) {
    max-height: none;
  }
}

.wc-order-type-cart-picker :deep(.modal-header) {
  border-bottom: none;
  padding: 0.75rem 1.25rem 0;
}

.wc-order-type-cart-picker :deep(.modal-body) {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0 0 1.25rem;
  overflow-y: visible;
}

@include media-breakpoint-up(md) {
  .wc-order-type-cart-picker :deep(.modal-header) {
    padding: 0.75rem 1.5rem 0;
  }

  .wc-order-type-cart-picker :deep(.modal-body) {
    display: block;
    padding: 0 0 1.5rem;
  }
}

.wc-order-type-cart-picker__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.wc-order-type-cart-picker__title {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--dark, $dark);
  margin: 0;
}

.wc-order-type-cart-picker__item-name {
  font-size: 0.875rem;
  color: var(--dark-lighter-2);
  margin: 0;
  padding: 0.625rem 1.25rem 1rem;
  border-bottom: 1px solid var(--dark-lighter-9);
}

.wc-order-type-cart-picker__item-name strong {
  color: var(--dark, $dark);
}

@include media-breakpoint-up(md) {
  .wc-order-type-cart-picker__item-name {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}

.wc-order-type-cart-picker__close {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 50%;
  background: none;
  color: var(--dark, $dark);
  font-size: 0.875rem;
  cursor: pointer;
}

@media (hover: hover) {
  .wc-order-type-cart-picker__close:hover {
    color: var(--dark-lighter-3);
  }
}

.wc-order-type-cart-picker__handle {
  display: flex;
  justify-content: center;
  padding: 0.625rem 0 0;
}

@include media-breakpoint-up(md) {
  .wc-order-type-cart-picker__handle {
    display: none;
  }
}

.wc-order-type-cart-picker__handle-bar {
  width: 2.25rem;
  height: 0.25rem;
  border-radius: 0.125rem;
  background: var(--dark-lighter-7);
}

.wc-order-type-cart-picker__list {
  list-style: none;
  margin: 0;
  padding: 0 0.5rem 0 0;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

@include media-breakpoint-up(md) {
  .wc-order-type-cart-picker__list {
    flex: none;
    overflow-y: visible;
  }
}

.wc-order-type-cart-picker__reassure {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.375rem;
  padding: 0.75rem 1.25rem 0;
  font-size: 0.8125rem;
  color: var(--dark-lighter-3);
}

@include media-breakpoint-up(md) {
  .wc-order-type-cart-picker__reassure {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}

.wc-order-type-cart-picker__reassure svg {
  color: var(--dark-lighter-4);
  font-size: 0.75rem;
}
</style>
